/**
 * Utility functions for SDF algorithms.
 */

import { gl } from '../../../Engine';

/**
 * Reset GL capabilities to their expected default state.
 * Call this before yielding to the event loop during
 * long-running GL operations, and at the end of GL functions.
 *
 * This avoids expensive gl.getParameter() CPU-GPU sync points
 * that a save/restore pattern would require.
 */
export function resetGlDefaults(): void {
    gl.disable(gl.STENCIL_TEST);
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.BLEND);
    gl.blendEquation(gl.FUNC_ADD);
    gl.depthMask(true);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
}

/**
 * Reentrancy guard for async operations that use the shared
 * global WebGL context. Prevents overlapping GL operations
 * from corrupting each other's state.
 *
 * Usage:
 *   if (!GlOperationGuard.acquire('Gaussian smoothing')) return;
 *   try { ... } finally { GlOperationGuard.release(); }
 */
export class GlOperationGuard {
    private static activeOperation: string | null = null;

    /**
     * Try to acquire the guard. Returns true if acquired.
     *
     * A refusal is LOGGED as an error: overlapping GL operations are a
     * defect, and that line is the only record of one. A caller that
     * expects contention and answers it itself — waiting the holder out,
     * or reporting "the GPU is busy" in words — checks {@link isActive}
     * first instead of calling this and reading the refusal, so an
     * expected wait does not fill the console with defect reports.
     */
    public static acquire(operationName: string): boolean {
        if (GlOperationGuard.activeOperation) {
            console.error(
                `[GlOperationGuard] Cannot start "${operationName}": ` +
                    `"${GlOperationGuard.activeOperation}" is already running. ` +
                    `Concurrent async GL operations are not supported.`
            );
            return false;
        }
        GlOperationGuard.activeOperation = operationName;
        return true;
    }

    /** Release the guard. */
    public static release(): void {
        GlOperationGuard.activeOperation = null;
    }

    /** Check if a GL operation is currently running. */
    public static get isActive(): boolean {
        return GlOperationGuard.activeOperation !== null;
    }
}

/**
 * Default {@link maybeYield} budget: roughly one frame at 60 fps. The
 * previous 100 ms budget served pointer events at ≤10 Hz for the whole
 * duration of a multi-second compute — the direct cause of sluggish
 * color/slider dragging while a body-contour preview ran.
 */
const DEFAULT_YIELD_BUDGET_MS = 12;

/**
 * Yield one macrotask via a MessageChannel port message rather than
 * setTimeout(0): nested timers are clamped to ~4 ms by the HTML spec, so
 * a compute loop yielding every frame would burn a third of each frame
 * waiting on the clamp. A port message has no clamp — queued input and
 * rendering run, then the compute resumes immediately.
 */
function yieldToEventLoop(): Promise<void> {
    return new Promise<void>((resolve) => {
        const channel = new MessageChannel();
        channel.port1.onmessage = () => {
            channel.port1.close();
            resolve();
        };
        channel.port2.postMessage(null);
    });
}

/** The error every abortable operation in this module rejects with. */
export function abortError(): DOMException {
    return new DOMException('Operation aborted', 'AbortError');
}

export function isAbortError(error: unknown): boolean {
    return error instanceof DOMException && error.name === 'AbortError';
}

/** @throws the abort error once `signal` has fired. */
export function throwIfAborted(signal?: AbortSignal): void {
    if (signal?.aborted) {
        throw abortError();
    }
}

/**
 * Yield to event loop based on time budget.
 * Call this periodically in long-running operations to keep the UI responsive.
 *
 * @param lastYieldTick The timestamp of the last yield (from performance.now())
 * @param signal Optional AbortSignal to check for cancellation
 * @param budget Time budget in ms before yielding (default: one frame)
 * @returns The new lastYieldTick value to use for the next call
 */
export async function maybeYield(
    lastYieldTick: number,
    signal?: AbortSignal,
    budget: number = DEFAULT_YIELD_BUDGET_MS
): Promise<number> {
    throwIfAborted(signal);
    const now = performance.now();
    if (now - lastYieldTick >= budget) {
        await yieldToEventLoop();
        return performance.now();
    }
    return lastYieldTick;
}
